/**
 * The profile's stat tiles, as data: the label, the text shown, and the line under it. The DOM half (`stat()` in
 * screens.ts) only draws these, so what a tile SAYS (the order, the thousands format, the dash for "none yet") is
 * held here and tested in node. Blockari's `ProfileStats` (348b47e) is the model: seven tiles, Avg score right
 * after High score, "Member since" closing the grid.
 */

import { streakMultiplierLabel } from '@/game/scoring';
import type { Profile } from '@/net/api';
import { formatScore } from '@/ui/hud';
import { lastSeenLabel } from '@/ui/last-seen';
import { hasStreaks } from '@/ui/streak-copy';

export interface ProfileTile {
  /** Which tile: High score is a button on your own page, so the screen needs to tell it apart. */
  readonly key: 'games' | 'record' | 'high' | 'avg' | 'level' | 'streak' | 'since';
  readonly label: string;
  readonly value: string;
  /** The small line under the figure ("NN% win rate"). */
  readonly unit?: string;
  /** The rank-coloured figure. */
  readonly hi?: boolean;
  /** Best streak, when there is a streaks sheet to open: the cue on the button tile ("Streaks"). */
  readonly more?: string;
}

/**
 * The mean final score, thousands-separated like every other figure here. A dash, never 0 or NaN, when the
 * server sent none: no game yet, or an older server that does not send it. A real average of 0 is a real 0.
 */
export function avgScoreLabel(avg: number | null | undefined): string {
  return typeof avg === 'number' && Number.isFinite(avg) ? formatScore(avg) : '—';
}

/**
 * "LV 7", or a dash when there is no level to show: the server sends null with no games, and an older server sent 1
 * for them (indistinguishable from a real level 1, so it is shown as is). Never "LV null", "LV 0" or "LV NaN".
 */
export function bestLevelLabel(level: number | null | undefined): string {
  return typeof level === 'number' && Number.isFinite(level) && level > 0 ? `LV ${level}` : '—';
}

/** `Sep 2026`, for "member since". */
export function monthOf(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/**
 * The tiles in the order Blockari shows them. Member since carries "Last seen …" under the month (Blockari's
 * `ProfileStats`, c258375) and nothing under it when the server sent none; Best streak carries the "Streaks" cue
 * when there is a sheet behind it.
 */
export function profileTiles(profile: Profile, now: Date = new Date()): ProfileTile[] {
  const won = profile.challengesWon;
  const played = profile.challengesPlayed;
  const lost = Math.max(0, played - won - (profile.challengesDrawn ?? 0));
  // Rounded, not floored: 2 of 3 is nearer 67% than 66%. Omitted at zero
  // played, where a percentage would be a claim about nothing.
  const lastSeen = lastSeenLabel(profile.lastSeenAt, now);
  const rate = played > 0 ? `${Math.round((won / played) * 100)}% win rate` : '';
  return [
    { key: 'games', label: 'Games played', value: formatScore(profile.gamesPlayed) },
    { key: 'record', label: 'Record', value: `${won}W – ${lost}L`, unit: rate },
    { key: 'high', label: 'High score', value: formatScore(profile.bestScore), hi: true },
    { key: 'avg', label: 'Avg score', value: avgScoreLabel(profile.avgScore) },
    { key: 'level', label: 'Best level', value: bestLevelLabel(profile.bestLevel) },
    {
      key: 'streak',
      label: 'Best streak',
      value: streakMultiplierLabel(profile.bestStreak ?? 0),
      ...(hasStreaks(profile) ? { more: 'Streaks' } : {}),
    },
    {
      key: 'since',
      label: 'Member since',
      value: monthOf(profile.createdAt),
      ...(lastSeen ? { unit: lastSeen } : {}),
    },
  ];
}
